import moment from "moment";
import { DataPartograf, Partograf } from "@/types/partograf";

export const SLOT_COUNT = 33;
export const GRID_MINUTES = 30;
export const SQL_FORMAT = "YYYY-MM-DD HH:mm:ss";

export function blankSlot(jamLabel: string): Partograf {
  return {
    jam: jamLabel,
    jam_aktual: null,
    djj: null,
    serviks: null,
    kepala: null,
    tv: { sistole: null, diastole: null, nadi: null },
    his: { durasi: null, kontraksi: null },
    air_ketuban: null,
    moulase: null,
    oksitosin_iu: null,
    oksitosin_tetes: null,
    obat_cairan_iv: null,
    suhu: null,
    protein_urin: null,
    aseton_urin: null,
    volume_urin: null,
    petugas_user_id: null,
    petugas_nama: null,
    petugas_at: null
  } as any;
}

const SKIP_KEYS = ["jam", "petugas_user_id", "petugas_nama", "petugas_at"];

export function isSlotEmpty(slot: any): boolean {
  if (!slot) return true;
  for (const key of Object.keys(slot)) {
    if (SKIP_KEYS.indexOf(key) !== -1) continue;
    const val = slot[key];
    if (val && typeof val === "object") {
      if (!isSlotEmpty(val)) return false;
      continue;
    }
    if (val !== null && val !== undefined && String(val).trim() !== "") return false;
  }
  return true;
}

export function toMoment(val: any): any {
  if (!val) return null;
  const m = moment(val);
  return m.isValid() ? m : null;
}

export function toSqlDateTime(val: any): string | null {
  const m = toMoment(val);
  return m ? m.format(SQL_FORMAT) : null;
}

export function toHm(val: any): string | null {
  const m = toMoment(val);
  return m ? m.format("HH:mm") : null;
}

export function hmToDate(val: any): Date | null {
  if (!val) return null;
  if (val instanceof Date) return val;
  const m = moment(String(val), ["HH:mm:ss", "HH:mm"], true);
  if (!m.isValid()) return null;
  const now = moment();
  return m.year(now.year()).month(now.month()).date(now.date()).toDate();
}

export function dateToHm(val: any): string | null {
  if (!val) return null;
  if (typeof val === "string" && /^\d{2}:\d{2}/.test(val)) return val.substr(0, 5);
  const m = toMoment(val);
  return m ? m.format("HH:mm") : null;
}

export function floorToHalfHour(m: any): any {
  const out = m.clone();
  out.minutes(out.minutes() < GRID_MINUTES ? 0 : GRID_MINUTES);
  out.seconds(0).milliseconds(0);
  return out;
}

export function slotLabel(anchor: any, index: number): string {
  return anchor.clone().add(index * GRID_MINUTES, "minutes").format("HH:mm:ss");
}

export function buildSlots(anchor: any): Partograf[] {
  const slots: Partograf[] = [];
  for (let i = 0; i < SLOT_COUNT; i++) slots.push(blankSlot(slotLabel(anchor, i)));
  return slots;
}

export function anchorOf(data: DataPartograf | null): any {
  if (!data) return null;
  const stored = toMoment(data.waktu_mulai_observasi);
  if (stored) return stored;

  const rows: any[] = data.partograf || [];
  if (!rows.length || !rows[0] || !rows[0].jam) return null;

  const tanggal = data.tanggal ? moment(data.tanggal).format("YYYY-MM-DD") : moment().format("YYYY-MM-DD");
  return toMoment(tanggal + " " + rows[0].jam);
}

export function hasStoredAnchor(data: DataPartograf | null): boolean {
  return !!(data && toMoment(data.waktu_mulai_observasi));
}

export function gridRangeLabel(anchor: any): string {
  const end = anchor.clone().add((SLOT_COUNT - 1) * GRID_MINUTES, "minutes");
  return anchor.format("DD-MM-YYYY HH:mm") + " s.d. " + end.format("DD-MM-YYYY HH:mm");
}

export function kolomLabel(anchor: any, index: number): string {
  const start = anchor.clone().add(index * GRID_MINUTES, "minutes");
  const end = start.clone().add(GRID_MINUTES, "minutes");
  return start.format("HH:mm") + "–" + end.format("HH:mm");
}

export function resolveIndex(anchor: any, waktu: any): { index?: number; aktual?: any; error?: string } {
  const aktual = toMoment(waktu);
  if (!anchor) return { error: "Awal observasi belum diketahui." };
  if (!aktual) return { error: "Jam observasi wajib diisi terlebih dahulu." };

  const selisih = aktual.diff(anchor, "minutes");
  if (selisih < 0) {
    return {
      error:
        "Jam " +
        aktual.format("DD-MM-YYYY HH:mm") +
        " lebih awal dari awal observasi (" +
        anchor.format("DD-MM-YYYY HH:mm") +
        ")."
    };
  }

  const index = Math.floor(selisih / GRID_MINUTES);
  if (index > SLOT_COUNT - 1) {
    return {
      error:
        "Jam " +
        aktual.format("DD-MM-YYYY HH:mm") +
        " di luar jangkauan grid partograf (" +
        gridRangeLabel(anchor) +
        ")."
    };
  }

  return { index, aktual };
}

export function pembuatLabel(data: any): string {
  if (!data) return "-";
  const pembuat = data.pembuat || {};
  const identitas = pembuat.identitas || {};
  const nama = data.pembuat_nama || identitas.nama || pembuat.username || "";
  const dibuat = toMoment(data.created_at);
  const waktu = dibuat ? dibuat.format("DD-MM-YYYY HH:mm") : "";
  if (!nama && !waktu) return "-";
  return (nama || "-") + (waktu ? " · " + waktu : "");
}

export function petugasNama(slot: any): string {
  if (!slot) return "-";
  const nama = slot.petugas_nama;
  return nama && String(nama).trim() !== "" ? String(nama) : "-";
}

export function jamTampil(slot: any): string {
  const aktual = toHm(slot ? slot.jam_aktual : null);
  if (aktual) return aktual;
  const jam = slot && slot.jam ? String(slot.jam) : "";
  return jam ? jam.substr(0, 5) : "-";
}

export function splitDurasi(total: number | null): { jam: number | null; menit: number | null } {
  if (total === null || total === undefined || total === ("" as any)) return { jam: null, menit: null };
  const t = Number(total);
  if (isNaN(t)) return { jam: null, menit: null };
  return { jam: Math.floor(t / 60), menit: t % 60 };
}

export function joinDurasi(jam: number | null, menit: number | null): number | null {
  if ((jam === null || jam === undefined) && (menit === null || menit === undefined)) return null;
  return (Number(jam) || 0) * 60 + (Number(menit) || 0);
}

export function formatDurasi(total: number | null): string {
  if (total === null || total === undefined || total === ("" as any)) return "";
  const { jam, menit } = splitDurasi(total);
  if (jam === null) return "";
  return jam > 0 ? jam + " jam " + menit + " menit" : menit + " menit";
}
